'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface UserMenuDropdownProps {
  className?: string;
}

const UserMenuDropdown = ({ className = '' }: UserMenuDropdownProps) => {
  const router = useRouter();
  const { user, userProfile, signOut, loading, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const menuItems = [
    { label: 'Suscripción', path: '/account-settings?tab=subscription', icon: 'CreditCardIcon' },
    { label: 'Ayuda y soporte', path: '/feedback-collection', icon: 'QuestionMarkCircleIcon' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    setIsOpen(false);
    const { error } = await signOut();
    
    if (!error) {
      router.push('/landing-page');
    }
  };

  if (loading || !isAuthenticated) {
    return null;
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-md hover:bg-muted transition-smooth"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="UserCircleIcon" size={32} className="text-primary" />
        </div>
        <Icon name="ChevronDownIcon" size={16} className={`transition-smooth ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-popover border border-border rounded-md shadow-elevation-lg z-1010">
          <div className="p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="UserCircleIcon" size={40} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-popover-foreground truncate">
                  {userProfile?.full_name || user?.email || 'Usuario'}
                </p>
                <p className="caption text-muted-foreground truncate">
                  {user?.email || ''}
                </p>
              </div>
            </div>
          </div>

          <div className="py-2">
            <Link
              href="/account-settings"
              onClick={() => setIsOpen(false)}
              className="flex items-center space-x-3 px-4 py-3 hover:bg-muted rounded-md transition-smooth"
            >
              <Icon name="Cog6ToothIcon" size={20} className="text-muted-foreground" />
              <span className="text-foreground font-medium">Configuración</span>
            </Link>
            {menuItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-3 px-4 py-2 hover:bg-muted transition-smooth"
              >
                <Icon name={item.icon as any} size={20} className="text-muted-foreground" />
                <span className="text-popover-foreground">{item.label}</span>
              </Link>
            ))}
          </div>

          <div className="border-t border-border p-2">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 w-full px-4 py-2 text-left hover:bg-muted rounded-md transition-smooth"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={20} className="text-error" />
              <span className="text-error font-medium">Cerrar sesión</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenuDropdown;
